/**
 * useCreateSupportTicket Hook
 *
 * React Query mutation for creating a new support ticket.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { supportTicketService } from '../../../api/support-ticket.service';
import type {
  SupportTicketWithMessage,
  SupportTicketCreateDTO,
} from '../../../types/support-ticket.types';

interface UseCreateSupportTicketOptions {
  tenantId: string;
  onSuccess?: (data: SupportTicketWithMessage) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook for creating a new support ticket
 */
export const useCreateSupportTicket = (
  options: UseCreateSupportTicketOptions
): UseMutationResult<SupportTicketWithMessage, Error, SupportTicketCreateDTO> => {
  const { tenantId, onSuccess, onError } = options;
  const queryClient = useQueryClient();

  return useMutation<SupportTicketWithMessage, Error, SupportTicketCreateDTO>({
    mutationFn: async (data: SupportTicketCreateDTO) => {
      return await supportTicketService.createTicket(tenantId, data);
    },
    onSuccess: (data) => {
      // Invalidate tickets list query
      queryClient.invalidateQueries({ queryKey: ['support-tickets', tenantId] });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
